import { getSupabaseClient } from './supabase';
import { fetchTransferAvailabilityBatch } from './transfers';
import { runSupabaseQuery } from '../utils/appErrors';

/**
 * Warehouse › Requests: transfer broadcasts (shortage requests) for the viewer's sites, with the
 * event each one serves and the free quantity at the candidate site. Moved from the Warehouse page;
 * the status filter is now the request's own status (open, accepted, …) rather than the transfer
 * statuses, which never matched, and the offers query the page never showed is gone.
 */

/** Values of the `broadcast_status` enum, in its own order. */
export const TRANSFER_REQUEST_STATUSES = [
  'open',
  'awarded',
  'cancelled',
  'expired',
  'fulfilled',
  'accepted',
  'rejected',
] as const;

export type TransferRequestsPageParams = {
  /** The viewer's active location(s): requests to or from these sites are listed. */
  activeLocationIds: string[];
  allowedLocationIds: string[];
  offset: number;
  pageSize: number;
  search?: string;
  fromLocationIds?: string[] | null;
  toLocationIds?: string[] | null;
  needByFrom?: string;
  needByTo?: string;
  createdFrom?: string;
  createdTo?: string;
  linkedEventId?: string | null;
  /** Request statuses; null lists every status. Callers skip the request when none is picked. */
  statuses: string[] | null;
};

// Rows keep the loosely typed shape the Warehouse page has always rendered.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TransferRequestRow = any;

/** The start of a `YYYY-MM-DD` day in the viewer's own time zone, as an ISO instant. */
function localDayStartIso(day: string, addDays = 0): string {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(year, (month || 1) - 1, (date || 1) + addDays).toISOString();
}

/**
 * `column.ilike."%term%"` for a PostgREST `or()` list. LIKE wildcards in the term are escaped,
 * then the value is quoted, so commas, parentheses and quotes in a search stay literal.
 */
function orIlike(column: string, term: string): string {
  const like = term.replace(/[\\%_]/g, '\\$&');
  const quoted = like.replace(/[\\"]/g, '\\$&');
  return `${column}.ilike."%${quoted}%"`;
}

function chunk(ids: string[], size: number): string[][] {
  const groups: string[][] = [];
  for (let i = 0; i < ids.length; i += size) groups.push(ids.slice(i, i + size));
  return groups;
}

/** Shortages an event search can match; their ids travel in the request URL. */
const SEARCH_SHORTAGE_LIMIT = 100;

export async function fetchTransferRequestsPage(
  params: TransferRequestsPageParams,
): Promise<{ data: TransferRequestRow[]; count: number }> {
  const supabase = getSupabaseClient();
  const {
    activeLocationIds,
    allowedLocationIds,
    offset,
    pageSize,
    fromLocationIds,
    toLocationIds,
    needByFrom,
    needByTo,
    createdFrom,
    createdTo,
    linkedEventId,
    statuses,
  } = params;
  if (statuses && statuses.length === 0) return { data: [], count: 0 };

  const locationIdsToUse = activeLocationIds.length > 0 ? activeLocationIds : allowedLocationIds;
  if (locationIdsToUse.length === 0) return { data: [], count: 0 };

  const searchTerm = (params.search ?? '').trim();
  let linkedEventLineIds: string[] | null = null;
  if (linkedEventId) {
    const { data: eventLinesForLinkedEvent, error: linkedEventLinesError } = await supabase
      .from('event_lines')
      .select('event_line_id')
      .eq('event_id', linkedEventId);
    if (linkedEventLinesError) throw linkedEventLinesError;
    linkedEventLineIds = (eventLinesForLinkedEvent ?? [])
      .map((row: { event_line_id: string | null }) => row.event_line_id)
      .filter((id): id is string => Boolean(id));
    if (linkedEventLineIds.length === 0) return { data: [], count: 0 };
  }

  // An event's code or name reaches its requests through their shortages: the matching events, their
  // lines, then those lines' shortages (at most SEARCH_SHORTAGE_LIMIT, which keeps the request URL
  // short). The item and the fulfilling site are matched in the request read itself, below.
  const searchShortageIds: string[] = [];
  if (searchTerm) {
    const { data: matchingEvents, error: matchingEventsError } = await supabase
      .from('events')
      .select('id')
      .or([orIlike('event_code', searchTerm), orIlike('name', searchTerm)].join(','))
      .limit(200);
    if (matchingEventsError) throw matchingEventsError;
    const eventIds = (matchingEvents ?? []).map((row: { id: string | null }) => row.id).filter(Boolean);
    if (eventIds.length > 0) {
      const { data: matchingEventLines, error: matchingEventLinesError } = await supabase
        .from('event_lines')
        .select('event_line_id')
        .in('event_id', eventIds);
      if (matchingEventLinesError) throw matchingEventLinesError;
      const searchEventLineIds = (matchingEventLines ?? [])
        .map((row: { event_line_id: string | null }) => row.event_line_id)
        .filter((id): id is string => Boolean(id));
      for (const eventLineIdsChunk of chunk(searchEventLineIds, 200)) {
        const room = SEARCH_SHORTAGE_LIMIT - searchShortageIds.length;
        if (room <= 0) break;
        const { data: matchingShortages, error: matchingShortagesError } = await supabase
          .from('event_line_shortages')
          .select('shortage_id')
          .in('event_line_id', eventLineIdsChunk)
          .limit(room);
        if (matchingShortagesError) throw matchingShortagesError;
        (matchingShortages ?? []).forEach((row: { shortage_id: string | null }) => {
          if (row.shortage_id) searchShortageIds.push(row.shortage_id);
        });
      }
    }
  }

  // PostgREST refuses a column of an embedded table inside a top-level or() (PGRST100), so each
  // condition on a related row gets its own filtered embed, which is null when the row does not
  // match, and the or() only asks which of them matched. An alias is added only when it is filtered.
  const embeds = [
    'to_location:locations(location_id, name)',
    'item:items(item_id, item_code, name_internal)',
    'shortage:event_line_shortages!inner(shortage_id, event_line_id, item_id, location_id, qty_requested, qty_reserved, qty_short)',
    // The shortage again, kept only when one of the viewer's sites raised the request.
    'requesting:event_line_shortages(location_id)',
  ];
  if (searchTerm) embeds.push('search_item:items(item_id)', 'search_site:locations(location_id)');

  let baseQuery = supabase
    .from('transfer_broadcasts')
    .select(`
      broadcast_id,
      shortage_id,
      to_location_id,
      item_id,
      qty,
      needed_by_at,
      expected_return_at,
      status,
      created_at,
      ${embeds.join(',\n      ')}
    `, { count: 'exact' })
    .order('needed_by_at', { ascending: true })
    .order('broadcast_id', { ascending: true });

  if (statuses) baseQuery = baseQuery.in('status', statuses);
  if (fromLocationIds) baseQuery = baseQuery.in('shortage.location_id', fromLocationIds);
  if (toLocationIds) baseQuery = baseQuery.in('to_location_id', toLocationIds);
  // Day filters are the viewer's calendar days: [start of from-day, start of the day after to-day).
  if (needByFrom) baseQuery = baseQuery.gte('needed_by_at', localDayStartIso(needByFrom));
  if (needByTo) baseQuery = baseQuery.lt('needed_by_at', localDayStartIso(needByTo, 1));
  if (createdFrom) baseQuery = baseQuery.gte('created_at', localDayStartIso(createdFrom));
  if (createdTo) baseQuery = baseQuery.lt('created_at', localDayStartIso(createdTo, 1));
  if (linkedEventLineIds) baseQuery = baseQuery.in('shortage.event_line_id', linkedEventLineIds);
  if (searchTerm) {
    // `status` is an enum, so it is matched through the status filter, not the search.
    baseQuery = baseQuery
      .or([orIlike('item_code', searchTerm), orIlike('name_internal', searchTerm)].join(','), { referencedTable: 'search_item' })
      .or(orIlike('name', searchTerm), { referencedTable: 'search_site' });
    const searchMatches = ['search_item.not.is.null', 'search_site.not.is.null'];
    if (searchShortageIds.length > 0) searchMatches.push(`shortage_id.in.(${searchShortageIds.join(',')})`);
    baseQuery = baseQuery.or(searchMatches.join(','));
  }

  // The viewer's requests: one of their sites is asked to fill it, or one of their sites raised it.
  // (Separate or() parameters are ANDed, so this and the search's both apply.)
  const destinationLocationIds = activeLocationIds.length > 0 ? activeLocationIds : locationIdsToUse;
  baseQuery = baseQuery
    .in('requesting.location_id', locationIdsToUse)
    .or(`to_location_id.in.(${destinationLocationIds.join(',')}),requesting.not.is.null`);

  const { data: baseBroadcasts, error: baseError, count: baseCount } = await baseQuery.range(
    offset,
    offset + pageSize - 1,
  );
  if (baseError) throw baseError;

  const broadcastsArray: TransferRequestRow[] = Array.isArray(baseBroadcasts)
    ? baseBroadcasts.map((row: TransferRequestRow) => {
      const shortageLocationId = row?.shortage?.location_id ?? null;
      const isRequestingSite = shortageLocationId ? locationIdsToUse.includes(shortageLocationId) : false;
      return { ...row, isRequestingSite };
    })
    : [];
  if (broadcastsArray.length === 0) return { data: [], count: baseCount ?? 0 };

  // The event behind each request's shortage line.
  const eventLineIds = Array.from(new Set(
    broadcastsArray
      .map((row: TransferRequestRow) => row.shortage?.event_line_id)
      .filter((id: unknown): id is string => typeof id === 'string' && id.length > 0),
  ));
  const eventLinesMap = new Map<string, TransferRequestRow>();
  for (const eventLineIdsChunk of chunk(eventLineIds, 200)) {
    const { data: eventLinesData, error: eventLinesError } = await supabase
      .from('event_lines')
      .select(`
        event_line_id,
        event_id,
        item_id,
        qty,
        start_at,
        end_at,
        event:events(id, event_code, name, status)
      `)
      .in('event_line_id', eventLineIdsChunk);
    if (eventLinesError) throw eventLinesError;
    (eventLinesData || []).forEach((eventLine: TransferRequestRow) => {
      eventLinesMap.set(eventLine.event_line_id, eventLine);
    });
  }

  const mergedBroadcasts = broadcastsArray.map((request: TransferRequestRow) => {
    const eventLineId = request.shortage?.event_line_id;
    const shortage = eventLineId && eventLinesMap.has(eventLineId)
      ? { ...request.shortage, event_line: eventLinesMap.get(eventLineId) }
      : request.shortage;
    return { ...request, shortage };
  });

  // Free quantity at each request's candidate site for the requested window.
  const candidatesByLocation = new Map<string, TransferRequestRow[]>();
  mergedBroadcasts
    .filter((row: TransferRequestRow) => row.to_location_id && row.item_id && row.needed_by_at)
    .forEach((row: TransferRequestRow) => {
      const list = candidatesByLocation.get(row.to_location_id) ?? [];
      list.push({
        item_id: row.item_id,
        location_id: row.to_location_id,
        start_at: row.needed_by_at,
        end_at: row.expected_return_at || row.needed_by_at,
        current_qty: 0,
        exclude_order_id: null,
        exclude_event_id: null,
        candidate_id: row.broadcast_id,
      });
      candidatesByLocation.set(row.to_location_id, list);
    });
  const availabilityMap = new Map<string, number>();
  for (const [locationId, candidates] of candidatesByLocation.entries()) {
    const availability = await runSupabaseQuery(
      () => fetchTransferAvailabilityBatch(candidates, locationId),
      'Unable to load transfer availability.',
      'transfers.requests.availability',
    );
    if (Array.isArray(availability)) {
      availability.forEach((row: TransferRequestRow) => {
        if (row.candidate_id && row.available_qty != null) availabilityMap.set(row.candidate_id, row.available_qty);
      });
    }
  }

  return {
    data: mergedBroadcasts.map((request: TransferRequestRow) => ({
      ...request,
      qty_available: availabilityMap.get(request.broadcast_id) ?? null,
    })),
    count: baseCount ?? 0,
  };
}
